import React, { useEffect, useState } from 'react';
import WeatherCard from '../WeatherCard';
import DOMPurify from 'dompurify';
import { SearchResults } from '../search/SearchResults';
import { MemeRenderer } from '../meme/MemeComponents';
import { ColorPaletteResponse } from './ColorPaletteResponse';
import { FormattedAIResponse } from '../FormattedAIResponse';
import { type PerigonArticle } from '../../services/agents/newsAgent';
import { NewsCarousel } from './NewsCarousel';
import { aiChatService } from '../../services/supabaseServices';
import { Card } from '@/components/ui/card';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { GiphyResults } from "./GiphyResults";
import { FlowChartRenderer } from "./FlowChartRenderer";
import { PasswordGeneratorCard } from "./PasswordGeneratorCard";
import { QRCodeGeneratorCard } from "./QRCodeGeneratorCard";

interface LARAResultsRendererProps {
  results: any[];
  isDarkMode: boolean;
}

export const LARAResultsRenderer: React.FC<LARAResultsRendererProps> = ({
  results,
  isDarkMode
}) => {
  const [isTaskOverviewExpanded, setIsTaskOverviewExpanded] = useState(false);

  // Consolidated result header component
  const renderProgressiveHeader = () => {
    return (
      <div className="hidden">
      </div>
    );
  };

  const renderAgentResult = (result: any, index: number) => {
    if (!result) return null;

    // Create a unique key for each result
    const key = `lara-result-${index}-${result.taskId || index}`;

    // Handle different agent types based on their format
    switch (result.type) {
      case 'weather':
        const weatherData = result.data;

        // Transform ConsolidatedWeatherData to WeatherData format
        const transformedWeatherData = weatherData?.current ? {
          location: weatherData.location || 'Unknown Location',
          temperature: Math.round(weatherData.current.temperature || weatherData.current.temp || 0),
          description: weatherData.current.description || weatherData.current.weather?.[0]?.description || 'No description',
          humidity: weatherData.current.humidity || 0,
          windSpeed: Math.round(weatherData.current.windSpeed || weatherData.current.wind_speed || 0),
          pressure: weatherData.current.pressure || 0,
          feelsLike: Math.round(weatherData.current.feelsLike || weatherData.current.feels_like || 0),
          visibility: weatherData.current.visibility || null,
          icon: weatherData.current.icon || weatherData.current.weather?.[0]?.icon || '01d',
          backgroundImage: weatherData.current.backgroundImage || 'https://images.unsplash.com/photo-1504608524841-42fe6f032b4b?w=400&h=300&fit=crop',
          isDay: weatherData.current.isDay !== undefined ? weatherData.current.isDay : true,
          condition: weatherData.current.condition || weatherData.current.weather?.[0]?.main || 'Clear',
          uvIndex: weatherData.uvIndex || weatherData.current.uvIndex,
          airQuality: weatherData.airQuality?.description,
          tempMax: weatherData.current.tempMax || weatherData.current.temp_max || weatherData.daily?.[0]?.temp_max || weatherData.daily?.[0]?.tempMax || (weatherData.current.temperature ? weatherData.current.temperature + 3 : null),
          tempMin: weatherData.current.tempMin || weatherData.current.temp_min || weatherData.daily?.[0]?.temp_min || weatherData.daily?.[0]?.tempMin || (weatherData.current.temperature ? weatherData.current.temperature - 5 : null),
          rain: weatherData.current.rain || weatherData.current.rain?.['1h']
        } : null;

        return (
          <div key={key} className="mb-4">
            {renderProgressiveHeader()}
            {transformedWeatherData ? (
              <WeatherCard data={transformedWeatherData} />
            ) : weatherData ? (
              <Card className="p-4">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  <h4 className="font-medium mb-2">Weather Data (Raw)</h4>
                  <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded overflow-x-auto">
                    {JSON.stringify(weatherData, null, 2)}
                  </pre>
                </div>
              </Card>
            ) : (
              <Card className="p-4">
                <p className="text-sm text-gray-500">Weather data not available</p>
              </Card>
            )}
          </div>
        );

      case 'meme-templates':
      case 'meme-result':
      case 'meme-error':
        return (
          <div key={key} className="mb-4">
            {renderProgressiveHeader()}
            <MemeRenderer
              content={JSON.stringify(result)}
              isDarkMode={isDarkMode}
            />
          </div>
        );

      case 'web-search':
        return (
          <div key={key} className="mb-4">
            {renderProgressiveHeader()}
            <SearchResults
              results={result.data}
              isDarkMode={isDarkMode}
            />
          </div>
        );

      case 'news':
        const newsData = result.data;
        return (
          <div key={key} className="mb-4">
            {renderProgressiveHeader()}



            {/* News Articles Carousel */}
            {newsData?.articles && newsData.articles.length > 0 && (
              <div className="mb-4">
                <NewsCarousel
                  articles={newsData.articles}
                  isDarkMode={isDarkMode}
                />
              </div>
            )}

            {/* Fallback for empty results */}
            {(!newsData || (!newsData.articles?.length && !newsData.summary)) && (
              <Card className="p-4">
                <p className="text-sm text-gray-500">No news articles found for this query</p>
              </Card>
            )}
          </div>
        );

      case 'color-palette':
        // Check if data is HTML string or needs ColorPaletteResponse component
        if (typeof result.data === 'string' && result.data.includes('<div')) {
          // If it's an HTML string from the agent, render it directly
          return (
            <div key={key} className="mb-4">
              {renderProgressiveHeader()}
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(result.data) }} />
            </div>
          );
        } else {
          // Otherwise use ColorPaletteResponse component
          return (
            <div key={key} className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="text-sm font-medium">Color Palette Generator</span>
              </div>
              <ColorPaletteResponse
                palette={result.data}
                isDarkMode={isDarkMode}
              />
            </div>
          );
        }

      case 'image-search':
        return (
          <div key={key} className="mb-4">
            {renderProgressiveHeader()}
            <div className="columns-2 md:columns-3 gap-4">
              {result.data?.hits?.slice(0, 9).map((image: any, idx: number) => (
                <div key={idx} className="mb-4 break-inside-avoid">
                  <img
                    src={image.webformatURL}
                    alt={image.tags}
                    className="w-full rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => window.open(image.largeImageURL, '_blank')}
                  />
                </div>
              ))}
            </div>
          </div>
        );

      case 'youtube':
        // YouTube service returns { videos: [], channels: [], totalResults: number, query: string }
        const youtubeData = result.data;
        if (!youtubeData || (!youtubeData.videos && !youtubeData.items && !youtubeData.results)) {
          return (
            <div key={key} className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="text-sm font-medium">YouTube Search</span>
              </div>
              <Card className="p-4">
                <p className="text-sm text-gray-500">No videos found</p>
              </Card>
            </div>
          );
        }

        // Handle different YouTube data structures
        const videos = youtubeData.videos || youtubeData.items || youtubeData.results || [];

        return (
          <div key={key} className="mb-4">
            {renderProgressiveHeader()}
            <div className="space-y-3">
              {videos.slice(0, 4).map((video: any, idx: number) => (
                <Card key={idx} className="p-4">
                  <a
                    href={video.url || `https://youtube.com/watch?v=${video.id?.videoId || video.videoId || video.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex gap-3 hover:opacity-80 transition-opacity"
                  >
                    <img
                      src={video.thumbnail || video.snippet?.thumbnails?.medium?.url || video.snippet?.thumbnails?.default?.url || ''}
                      alt={video.title || video.snippet?.title || 'Video'}
                      className="w-32 h-24 object-cover rounded flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm line-clamp-2 mb-2">{video.title || video.snippet?.title || 'Untitled'}</h4>
                      <p className="text-xs text-gray-500 mb-1">{video.channelTitle || video.snippet?.channelTitle || 'Unknown Channel'}</p>
                      {video.viewCount && <p className="text-xs text-gray-400">{video.viewCount}</p>}
                      {video.duration && <p className="text-xs text-gray-400">{video.duration}</p>}
                    </div>
                  </a>
                </Card>
              ))}
            </div>
          </div>
        );

      case 'recipe':
        const recipeContent = typeof result.data === 'string'
          ? result.data
          : JSON.stringify(result.data, null, 2);
        return (
          <div key={key} className="mb-4">
            {renderProgressiveHeader()}
            <Card className="p-4">
              <FormattedAIResponse
                content={recipeContent}
                isDarkMode={isDarkMode}
                isStreaming={false}
              />
            </Card>
          </div>
        );

      case 'gif':
        {
          const rawGifs = result.data?.gifs || result.data?.data || result.data || [];
          const mapped = (Array.isArray(rawGifs) ? rawGifs : []).map((g: any, i: number) => ({
            id: g.id || String(i),
            title: g.title || 'GIF',
            url: g.url || g.images?.original?.url || g.images?.fixed_height?.url || g.preview || (typeof g === 'string' ? g : ''),
            preview: g.preview || g.images?.preview_gif?.url || g.images?.fixed_height_small?.url || g.url || (typeof g === 'string' ? g : ''),
          })).filter((g: any) => g.url);

          return (
            <div key={key} className="mb-4">
              {renderProgressiveHeader()}
              <GiphyResults gifs={mapped} query={result.agentData?.query || 'GIFs'} isDarkMode={isDarkMode} />
            </div>
          );
        }

      case 'password':
        const passwordData = typeof result.data === 'object' && result.data !== null
          ? result.data
          : { password: String(result.data), options: undefined };
        return (
          <div key={key} className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-sm font-medium">Password Generator</span>
            </div>
            <PasswordGeneratorCard
              initialPassword={passwordData.password}
              initialOptions={passwordData.options}
              isDarkMode={isDarkMode}
            />
          </div>
        );

      case 'qr-code':
        const qrData = result.data;
        return (
          <div key={key} className="mb-4">
            <QRCodeGeneratorCard
              text={qrData?.text || ''}
              qrUrl={qrData?.url || qrData?.qrUrl || ''}
              isDarkMode={isDarkMode}
            />
          </div>
        );

      case 'unit-converter':
        const convertContent = result.data;
        let displayContent = '';

        if (typeof convertContent === 'object' && convertContent !== null) {
          // If it's a conversion result object
          if (convertContent.from && convertContent.to && convertContent.value !== undefined && convertContent.result !== undefined) {
            displayContent = `**${convertContent.value} ${convertContent.from} = ${convertContent.result} ${convertContent.to}**\n\nCategory: ${convertContent.category || 'General'}`;
          } else {
            displayContent = JSON.stringify(convertContent, null, 2);
          }
        } else if (typeof convertContent === 'string') {
          displayContent = convertContent;
        } else {
          displayContent = 'Conversion completed';
        }

        return (
          <div key={key} className="mb-4">
            {renderProgressiveHeader()}
            <Card className="p-4">
              <FormattedAIResponse
                content={displayContent}
                isDarkMode={isDarkMode}
                isStreaming={false}
              />
            </Card>
          </div>
        );

      case 'wikipedia':
        const wikiData = result.data;
        if (wikiData && typeof wikiData === 'object' && wikiData.title && wikiData.extract) {
          // Rich Wikipedia data with images
          return (
            <div key={key} className="mb-4">
              {renderProgressiveHeader()}
              <Card className="p-0 overflow-hidden">
                <div className={`bg-transparent p-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/thumb/8/80/Wikipedia-logo-v2.svg/103px-Wikipedia-logo-v2.svg.png"
                      alt="Wikipedia"
                      className="w-8 h-8"
                    />
                    <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{wikiData.title}</h3>
                  </div>
                  <p className={`leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{wikiData.extract}</p>
                </div>

                {wikiData.images && wikiData.images.length > 0 && (
                  <div className="p-4 bg-transparent">
                    <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">
                      📷 Related Images ({wikiData.images.length})
                    </h4>
                    <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
                      {wikiData.images.slice(0, 5).map((image: string, idx: number) => (
                        <img
                          key={idx}
                          src={image}
                          alt={`Wikipedia image ${idx + 1}`}
                          className="flex-shrink-0 w-40 h-30 object-cover rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer transition-transform"
                          onClick={() => {
                            const modal = document.createElement('div');
                            modal.className = 'fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4 cursor-pointer';
                            modal.onclick = () => modal.remove();

                            const img = document.createElement('img');
                            img.src = image;
                            img.className = 'max-w-full max-h-[80vh] object-contain rounded-lg';

                            modal.appendChild(img);
                            document.body.appendChild(modal);
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div className="p-4 text-center">
                  <a
                    href={wikiData.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-white hover:underline font-medium"
                  >
                    Read Full Wikipedia Article<svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M5 12h14M13 6l6 6-6 6" />
                    </svg>
                  </a>
                </div>
              </Card>
            </div>
          );
        } else {
          // Fallback to text content
          let wikiContent = '';
          if (typeof result.data === 'string') {
            wikiContent = result.data;
          } else if (result.data?.content) {
            wikiContent = result.data.content;
          } else if (result.data) {
            wikiContent = JSON.stringify(result.data, null, 2);
          }
          return (
            <div key={key} className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="text-sm font-medium">Wikipedia</span>
              </div>
              <Card className="p-4">
                <FormattedAIResponse
                  content={wikiContent || 'No Wikipedia content found'}
                  isDarkMode={isDarkMode}
                  isStreaming={false}
                />
              </Card>
            </div>
          );
        }

      case 'error':
        return (
          <div key={key} className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-red-500" />
              <span className="text-sm font-medium text-red-500">
                {result.agentName} - Failed
              </span>
            </div>
            <Card className="p-4 border-red-200 bg-red-50 dark:bg-red-900/20">
              <p className="text-sm">{result.content}</p>
            </Card>
          </div>
        );

      // Handle calculator with explanations
      case 'calculator':
        const calcData = result.data;
        const hasExplanation = calcData && typeof calcData === 'object' && calcData.explanation;

        if (hasExplanation) {
          return (
            <div key={key} className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="text-sm font-medium">Calculator</span>
              </div>
              <Card className="p-4 space-y-3">
                <div className="bg-transparent p-3 rounded-lg">
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Expression:</div>
                  <div className="font-mono text-blue-600 dark:text-blue-400">{calcData.expression}</div>
                </div>
                <div className="bg-transparent p-3 rounded-lg">
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Result:</div>
                  <div className="text-xl font-bold text-green-600 dark:text-green-400">{calcData.result}</div>
                </div>
                {calcData.explanation && (
                  <div className="bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Explanation:</div>
                    <div className="text-sm text-gray-700 dark:text-gray-300">{calcData.explanation}</div>
                  </div>
                )}
              </Card>
            </div>
          );
        }
      // Fall through to default text handling for legacy calculator results

      // Handle flow/flowchart agent
      case 'flow':
        return (
          <div key={key} className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-sm font-medium">📊 Flow Chart Generator</span>
            </div>
            <FlowChartRenderer
              spec={result.data}
              isDarkMode={isDarkMode}
            />
          </div>
        );

      // Handle translator, code, unit-converter and other text-based agents
      case 'translator':
      case 'code':
        const textContent = typeof result.data === 'string'
          ? result.data
          : result.data?.content || result.data?.result || JSON.stringify(result.data);
        return (
          <div key={key} className="mb-4">
            {renderProgressiveHeader()}
            <Card className="p-4">
              <FormattedAIResponse
                content={textContent}
                isDarkMode={isDarkMode}
                isStreaming={false}
              />
            </Card>
          </div>
        );

      default:
        // For any other responses
        if (typeof result === 'string' || result.content || result.data) {
          const defaultContent = result.content || result.data || result;
          return (
            <div key={key} className="mb-4">
              {renderProgressiveHeader()}
              <Card className="p-4">
                <FormattedAIResponse
                  content={typeof defaultContent === 'string' ? defaultContent : JSON.stringify(defaultContent, null, 2)}
                  isDarkMode={isDarkMode}
                  isStreaming={false}
                />
              </Card>
            </div>
          );
        }
        return null;
    }
  };

  return (
    <div className="space-y-4">


      {/* Render all agent results */}
      <div className="space-y-4">
        {results.map((result, index) => renderAgentResult(result, index))}
      </div>

      {/* Task Overview at the bottom */}
      {/* <div className={`border-none pt-2 border-t border-gray-600`}>
        <button
          onClick={() => setIsTaskOverviewExpanded(!isTaskOverviewExpanded)}
          className="w-[max-content] px-3 py-1 pt-2 flex items-center justify-center gap-3 transition-colors"
        >
          <span className="text-sm font-medium darkk:text-gray-300 text-gray-600">Task Overview</span>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isTaskOverviewExpanded ? 'rotate-180' : ''}`} />
        </button>
        {isTaskOverviewExpanded && (
          <div className="p-3 pb-0 border-t border-gray-600 dark:bg-gray-800/20 bg-gray-50">
            <div className="space-y-2">
              {results.map((result, index) => {
                const taskName = result.agentData?.taskName || result.type || `Task ${index + 1}`;
                const status = result.status || 'completed';
                const statusEmojis = {
                  pending: '⏳',
                  executing: '🔄', 
                  completed: '✅',
                  failed: '❌'
                };
                
                return (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    <span>{statusEmojis[status as keyof typeof statusEmojis] || '✅'}</span>
                    <span className="dark:text-gray-300 text-gray-600">{taskName}</span>
                    <span className="text-gray-500">- {status}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div> */}
    </div>
  );
};

export default LARAResultsRenderer;
